import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useTranslation, Trans } from 'react-i18next';
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap';
import { get } from 'lodash';

import { useCelebrityQuery } from '../hooks';
import CelebrityImage from './Celebrity/CelebrityImage';
import {
  setCurrentField,
  setCurrentCountry,
  vote,
} from '../actions';
import {
  getCurrentField,
  getVotingCelebrityIds,
  getProfileIsFetching,
  getCurrentCountry,
  getVoteableCountries,
  getVoteableFields,
} from '../reducers';
import withEmailVerification from './withEmailVerification';
import { celebrityProfileUrl, isVowel } from '../helpers';
import CelebrityProfile from './Celebrity/CelebrityProfile';
import CelebrityCompare from './Celebrity/CelebrityCompare';
import Icon from './Icon';
import { translateFieldOptions, translateCountryOptions } from '../utils/fields';

function Vote() {
  const field = useSelector(getCurrentField);
  const country = useSelector(getCurrentCountry);
  const countries = useSelector(getVoteableCountries);
  const fields = useSelector(getVoteableFields);
  const { t, i18n } = useTranslation();
  const translatedCountries = useMemo(
    () => translateCountryOptions(countries, t, i18n.language || 'en'),
    [countries, t, i18n.language]
  );
  const translatedFields = useMemo(
    () => translateFieldOptions(fields, t),
    [fields, t]
  );
  const fieldsMap = useMemo(
    () =>
      translatedFields.reduce((obj, curr) => {
        obj[curr.value] = curr.label;
        return obj;
      }, {}),
    [translatedFields]
  );
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  useEffect(() => {
    if (
      countries.length !== 0 &&
      !countries.map(c => c.value).includes(country)
    ) {
      dispatch(setCurrentCountry('INTL'));
    }
  }, [country, countries, dispatch]);

  useEffect(() => {
    if (fields.length !== 0 && !fields.map(f => f.value).includes(field)) {
      dispatch(setCurrentField(fields[0].value));
    }
  }, [field, fields, dispatch]);

  useEffect(() => {
    dispatch(vote(field, country));
  }, [field, country, dispatch]);

  const celebrityIds = useSelector(getVotingCelebrityIds);
  const isProfileFetching = useSelector(getProfileIsFetching);

  const query = useMemo(
    () =>
      celebrityIds && {
        ids: celebrityIds,
        key: 'vote',
      },
    [celebrityIds]
  );

  const { celebrities, isFetching, error } = useCelebrityQuery(query);

  let celebrity1, celebrity2;
  if (celebrities.length === 2) {
    celebrity1 = celebrities[0];
    celebrity2 = celebrities[1];
  }
  function handleVote(event, winner) {
    event.preventDefault();
    if (!celebrity1 || !celebrity2) {
      return;
    }

    dispatch(vote(field, country, winner)).catch(error => {
      addToast(error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    });
  }

  function handleFieldChange(event) {
    dispatch(setCurrentField(event.target.value));
  }

  function handleCountryChange(event) {
    dispatch(setCurrentCountry(event.target.value));
  }

  function handleSkip(event) {
    event.preventDefault();
    dispatch(vote(field, country));
  }

  const imageContainerRef1 = useRef(null);
  const imageContainerRef2 = useRef(null);

  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(0);

  const currentFieldLabel = get(fieldsMap, field, '');
  const lowerCaseFieldLabel = currentFieldLabel.toLowerCase();
  const leadingChar = lowerCaseFieldLabel && lowerCaseFieldLabel[0] ? lowerCaseFieldLabel[0] : '';
  const articleKey = isVowel(leadingChar) ? 'vote.articleAn' : 'vote.articleA';
  const rawArticle = t(articleKey, { defaultValue: '' }).trim();
  const articleWithSpace = rawArticle ? `${rawArticle} ` : '';

  function toggleModal() {
    setModal(!modal);
  }

  function openModal(event, selected) {
    event && event.preventDefault();
    setSelected(selected);
    toggleModal();
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-12 col-sm-6 mb-2">
          <form className="form-inline d-flex justify-content-center">
            <label className="sr-only" htmlFor="country">
              {t('ratings.countryLabel')}
            </label>
            <select
              className="form-control mt-2 custom-select"
              id="country"
              name="country"
              onChange={handleCountryChange}
              value={country}
              style={{
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '16px 12px',
                paddingRight: '2rem',
                width: '300px'
              }}
            >
              {translatedCountries.map(countryOption => (
                <option key={countryOption.value} value={countryOption.value}>
                  {countryOption.label}
                </option>
              ))}
            </select>
          </form>
        </div>
        <div className="col-12 col-sm-6 mb-2">
          <form className="form-inline d-flex justify-content-center">
            <label className="sr-only" htmlFor="field">
              {t('ratings.fieldLabel')}
            </label>
            <select
              className="form-control mt-2 custom-select"
              id="field"
              name="field"
              onChange={handleFieldChange}
              value={field}
              style={{
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '16px 12px',
                paddingRight: '2rem',
                width: '300px'
              }}
            >
              {translatedFields
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(fieldOption => (
                  <option key={fieldOption.value} value={fieldOption.value}>
                    {fieldOption.label}
                  </option>
                ))}
            </select>
          </form>
        </div>
      </div>
      <div className="row">
        <div className="col text-center">
          <h1
            className="display-4" style={{ color: '#0A2342', fontWeight: 'bold' }}
          >
            <div className="d-none d-md-block">
              <Trans
                i18nKey="vote.questionDesktop"
                values={{ article: articleWithSpace, field: lowerCaseFieldLabel }}
                components={{ underline: <u /> }}
              />
              {' '}
              {t('vote.clickPhoto')}
            </div>
            <div className="d-block d-md-none">
              <div>{t('vote.questionMobileIntro')}</div>
              <div>
                <Trans
                  i18nKey="vote.questionMobile"
                  values={{ article: articleWithSpace, field: lowerCaseFieldLabel }}
                  components={{ underline: <u /> }}
                />
              </div>
              <div>{t('vote.clickPhoto')}</div>
            </div>
          </h1>
        </div>
      </div>
      {isFetching || isProfileFetching ? (
        <div className="row">
          <div className="col text-center">
            <div
              className="spinner-grow spinner-grow-lg text-primary"
              role="status"
            >
              <span className="sr-only">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="row">
          <div className="col text-center">
            <span className="text-danger">{error.message}</span>
          </div>
        </div>
      ) : !celebrity1 || !celebrity2 ? (
        <div className="row">
          <div className="col text-center">
            <span className="text-primary">
              {t('vote.emptyState')}
            </span>
            <span className="text-primary"></span>
          </div>
        </div>
      ) : (
        <>
          <div className="row">
            <div
              className="col-6 col-lg-4 offset-lg-2 d-flex justify-content-center pr-sm-2"
              ref={imageContainerRef1}
            >
              <a
                href="#left"
                onClick={event => handleVote(event, celebrityIds[0])}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <CelebrityImage
                  celebrity={celebrity1}
                  container={imageContainerRef1.current}
                  width={window.innerWidth < 576 ? 150 : 300}
                />
              </a>
            </div>
            <div
              className="col-6 col-lg-4 d-flex justify-content-center pl-sm-2"
              ref={imageContainerRef2}
            >
              <a
                href="#right"
                onClick={event => handleVote(event, celebrityIds[1])}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <CelebrityImage
                  celebrity={celebrity2}
                  container={imageContainerRef2.current}
                  width={window.innerWidth < 576 ? 150 : 300}
                />
              </a>
            </div>
          </div>
          <div className="row">
            <div
              className="col-6 col-lg-4 offset-lg-2 d-flex justify-content-end pr-sm-2"
              ref={imageContainerRef1}
            >
              <a
                href={celebrityProfileUrl(celebrity1.id, celebrity1)}
                onClick={e => openModal(e, 1)}
                style={{ width: '100%', textAlign: 'center' }}
              >
                {celebrity1.firstname} {celebrity1.lastname}
                <Icon icon="external-link-alt" className="ml-1" />
              </a>
            </div>
            <div
              className="col-6 col-lg-4 d-flex justify-content-start pl-sm-2"
              ref={imageContainerRef2}
            >
              <a
                href={celebrityProfileUrl(celebrity2.id, celebrity2)}
                onClick={e => openModal(e, 2)}
                style={{ width: '100%', textAlign: 'center' }}
              >
                {celebrity2.firstname} {celebrity2.lastname}
                <Icon icon="external-link-alt" className="ml-1" />
              </a>
            </div>
          </div>
          <Modal
            isOpen={modal}
            toggle={toggleModal}
            size="xl"
            scrollable={true}
            style={{ zIndex: 99999, marginTop: '70px' }}
            backdropStyle={{ zIndex: 99998 }}
            container={document.body}
          >
            <ModalBody>
              {selected === 0 ? (
                <CelebrityCompare
                  celebId1={celebrity1.id}
                  celebId2={celebrity2.id}
                />
              ) : (
                <CelebrityProfile
                  celebrityId={selected === 1 ? celebrity1.id : celebrity2.id}
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button 
                color="primary" 
                onClick={toggleModal}
                onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
                onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
                style={{ color: '#F5F5D1' }}
              >
                {t('vote.closeButton')}
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
      {!isFetching && !isProfileFetching && !!celebrity1 && !!celebrity2 && (
        <div className="row">
          <div className="col-sm-6 pr-sm-2">
            <form className="form-inline d-flex justify-content-end">
              <button
                className="form-control mt-2"
                style={{ minWidth: '100px' }}
                onClick={e => openModal(e, 0)}
              >
                {t('vote.compareButton')}
              </button>
            </form>
          </div>
          <div className="col-sm-6 pl-sm-2">
            <form className="form-inline d-flex justify-content-start">
              <button
                className="form-control mt-2"
                style={{ minWidth: '100px' }}
                onClick={handleSkip}
              >
                {t('vote.skipButton')}
              </button>
            </form>
          </div>
        </div>
      )}
      <div className="jumbotron jumbotron-fluid mb-3 mt-4">
        <div className="container">
          <h2 style={{ color: '#0A2342' }}>{t('vote.sectionTitle')}</h2>
          <p className="lead">
            {t('vote.sectionDescription')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default withEmailVerification(Vote);
